import { defineStore } from 'pinia';
import axios from 'axios';
import { ref, computed, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { useErrorLogger } from '@/composables/useErrorLogger';

export const useAuthStore = defineStore('auth', () => {
  const { logError } = useErrorLogger();
  // Cashier State
  const isUserLoggedIn = ref(localStorage.getItem('token') ? true : false);
  const currentUser = ref(localStorage.getItem('currentUser') || '');
  const userRole = ref(localStorage.getItem('userRole') || ''); 
  const token = ref(localStorage.getItem('token') || null);
  const canDoEmbedded = ref(localStorage.getItem('canDoEmbedded') === 'true');

  const refreshCanDoEmbedded = () => {
    canDoEmbedded.value = localStorage.getItem('canDoEmbedded') === 'true';
  };
  
  // Manager State
  const isManagerLoggedIn = ref(localStorage.getItem('managerToken') ? true : false);
  const managerUser = ref(localStorage.getItem('managerUser') || ''); 
  const managerRole = ref(localStorage.getItem('managerRole') || ''); 
  const managerToken = ref(localStorage.getItem('managerToken') || null);

  // SignalR branch registration timestamp
  const lastUpdatedOn = ref(
    localStorage.getItem('lastUpdatedOn')
      ? new Date(localStorage.getItem('lastUpdatedOn'))
      : null
  );

  // Stable token getter for SignalR
  const getActiveToken = () => managerToken.value || token.value;

  // Set last updated timestamp for SignalR sync
  function setLastUpdatedOn(date) {
    if (!date) return;

    const iso = new Date(date).toISOString();
    lastUpdatedOn.value = new Date(iso);
    localStorage.setItem('lastUpdatedOn', iso);
  }

  // Reset timestamp for new business day
  function resetLastUpdatedOnForNewDay() {
    lastUpdatedOn.value = null;
    localStorage.removeItem('lastUpdatedOn');
  }

  const usersList = ref([]);
  const managerUsersList = ref([]);
  const customersList = ref([]);
  const selectedCustomer = ref(null);

  const isLogoutConfirmationVisible = ref(false);
  const isAddManagerApprovalRequest = ref(false);
  const isCashierLoginInput = ref(true); 
  const isAddBehaviourPopup = ref(false);
  // const isAddItemPopup = ref(false);
  const isManagerLoginPopupVisible = ref(false);
  const isAddSalesPopupVisible = ref(false);
  const isAddCustomerPopupVisible = ref(false);
  const isCheckoutPopupVisible = ref(false);
  const showCommentsModal = ref(false); 
  const isAddManualOrderPopupVisible = ref(false);
  const isOpeningBalacePopupVisible = ref(false);
  const isClosingBalacePopupVisible = ref(false);

  const isAddCommentButtonMoved = ref(false);
  const isAddManagerApprovalButtonMoved = ref(false);
  const isWalkInCustomerButtonMoved = ref(false)
  const isBarberItemsPopupVisible = ref(false);

  const printLabelPopupVisible = ref(false);
  const barcodeGeneratorVisible = ref(false);
  const isErrorLogViewerVisible = ref(false);

  const salesPersons = ref([]);


  const branchId = localStorage.getItem('branchId');

  // Fetch Cashier
  async function fetchCashiers() {
    const toast = useToast();
    try {
      const response = await axios.get('/api/Users/BranchUsers', {
        headers: {
          'store-id': branchId 
        }
      });
      // All emplyees as a sales person
      const allUsers = response.data;
      salesPersons.value = allUsers;

      console.log('All Employees', salesPersons.value);

      const cashiers = response.data.filter(user => user.role === 'Cashier');
      usersList.value = cashiers;
    } catch (error) {
      toast.error('Failed to load users', error.message);
      logError({ callStack: error?.stack || String(error), errorDetails: { message: error?.message, httpStatus: error?.response?.status, component: 'authStore', action: 'fetchCashiers', branchId } });
    }
  }

  // Fetch Managers
  async function fetchManager() {
    const toast = useToast();
    try {
      const response = await axios.get('/api/Users/BranchUsers', {
        headers: {
          'store-id': branchId
        }
      });
      const managers = response.data.filter(user => user.role === 'Manager');
      managerUsersList.value = managers;
    } catch (error) {
      toast.error('Failed to load users', error.message);
      logError({ callStack: error?.stack || String(error), errorDetails: { message: error?.message, httpStatus: error?.response?.status, component: 'authStore', action: 'fetchManager', branchId } });
    }
  }

  // Login API function for both cashier and manager
  async function login(userId, pin) {
    const toast = useToast();
    try {
        if (!branchId) {
            throw new Error('Branch ID is missing');
        }

        const response = await axios.post('/api/Users/Login', {
            userId, 
            pin,    
        }, {
            headers: {
                'store-id': branchId, // Use branchId from URL parameters
            },
        });
        
        const { jwt: authToken, role, fullName } = response.data || {}; 
        
        if (!authToken || !role || !fullName) {
            throw new Error('Invalid response from server');
        }

        // Decode token to get CanDoEmbedded
        let extractedCanDoEmbedded = false;
        try {
            const [, payload] = authToken.split('.');
            const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
            extractedCanDoEmbedded = decoded.CanDoEmbedded === 'True';
        } catch (e) {
            console.error('Failed to decode token for CanDoEmbedded', e);
        }

        // Allow login only for Cashier or Manager
        if (role !== 'Cashier' && role !== 'Manager') {
            throw new Error('Unauthorized role');
        }

        // If cashier logs in
        if (role === 'Cashier') {
            localStorage.setItem('token', authToken);
            localStorage.setItem('currentUser', fullName);
            localStorage.setItem('userRole', role);
            localStorage.setItem('canDoEmbedded', extractedCanDoEmbedded);
            token.value = authToken;
            currentUser.value = fullName;
            userRole.value = role;
            canDoEmbedded.value = extractedCanDoEmbedded;
            isUserLoggedIn.value = true;
            toast.success(`Welcome back, ${fullName}`);
        }
    
        // If manager logs in
        if (role === 'Manager') {
            localStorage.setItem('managerToken', authToken);
            localStorage.setItem('managerUser', fullName);
            localStorage.setItem('managerRole', role);
            localStorage.setItem('canDoEmbedded', extractedCanDoEmbedded);
            managerToken.value = authToken;
            managerUser.value = fullName;
            canDoEmbedded.value = extractedCanDoEmbedded;
            managerRole.value = role;
            isManagerLoggedIn.value = true;
            toast.success(`Welcome back, ${fullName}`);
        }

        // reactive ref to avoid refresh issues 
        refreshCanDoEmbedded();

        console.log('Current User:', currentUser.value, 'Role:', userRole.value);
    } catch (error) {
        if (error.message === 'Unauthorized role') {
            toast.error('You are not authorized to log in.');
        } else {
            toast.error('Login failed!');
        }
        console.error('Login failed:', error.response?.data?.errors || error.message);
        logError({ callStack: error?.stack || String(error), errorDetails: { message: error?.message, httpStatus: error?.response?.status, component: 'authStore', action: 'login', userId } });
        throw error; 
    }
  }

  const isCurrentUserManager = computed(() => {
    const localStorageIsManager = localStorage.getItem('managerRole') === 'Manager';
    const storeIsManager = managerRole.value === 'Manager';
    
    console.log('Manager check - localStorage:', localStorageIsManager, 'store:', storeIsManager);
    return localStorageIsManager || storeIsManager;
  });
  
  // Add a manual refresh trigger
  const refreshAuthState = () => {
    managerRole.value = localStorage.getItem('managerRole') || '';
    isManagerLoggedIn.value = !!localStorage.getItem('managerToken');
  };

  // Logout Function
  function logout(role) {
    if (role === 'Cashier') {
      // Only log out the cashier
      isUserLoggedIn.value = false;
      currentUser.value = '';
      userRole.value = '';
      token.value = null;
      canDoEmbedded.value = false;
      localStorage.removeItem('token');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('userRole');
      // localStorage.removeItem('canDoEmbedded');

      isManagerLoggedIn.value = false;
      managerUser.value = '';
      managerRole.value = '';
      managerToken.value = null;
      localStorage.removeItem('managerToken');
      localStorage.removeItem('managerUser');
      localStorage.removeItem('managerRole');
      fetchCashiers();
    } else if (role === 'Manager') {
      // Only log out the manager
      isManagerLoggedIn.value = false;
      managerUser.value = '';
      managerRole.value = '';
      managerToken.value = null;
      canDoEmbedded.value = false;
      localStorage.removeItem('managerToken');
      localStorage.removeItem('managerUser');
      localStorage.removeItem('managerRole');
      // localStorage.removeItem('canDoEmbedded');
    }
  }

  // authStore.js or similar file
  function setLastUser(lastUser) {
    // Ensure the role is correctly set based on the last user
    currentUser.value = lastUser.currentUser || ''; 
    managerUser.value = lastUser.managerUser || '';
    userRole.value = lastUser.userRole || '';
    managerRole.value = lastUser.managerRole || '';
  }
  

  
  // Toggle Login input type
  function toggleLoginInput() {
    isCashierLoginInput.value = !isCashierLoginInput.value;
  }

  // Toggle Manager login popup
  function toggleManagerLoginPopup() {
    isManagerLoginPopupVisible.value = !isManagerLoginPopupVisible.value;
    console.log('Show login popup:', isManagerLoginPopupVisible.value);
  }

  // Toggle Add behaviour popup
  function toggleAddBehaviourPopup() {
    isAddBehaviourPopup.value = !isAddBehaviourPopup.value;
    console.log('Show popup:', isAddBehaviourPopup.value);
  }

  // Toggle Add item popup
  // function toggleAddItemPopup() {
  //   isAddItemPopup.value = !isAddItemPopup.value;
  //   console.log('Show popup:', isAddItemPopup.value);
  // }

  // Toggle Manager approval request popup
  function toggleAddManagerApprovalRequest() {
    isAddManagerApprovalRequest.value = !isAddManagerApprovalRequest.value;
    console.log('Show mng request popup:', isAddManagerApprovalRequest.value);
  }

  // Toggle Add sales popup
  function toggleAddSalesPopup () {
    isAddSalesPopupVisible.value = !isAddSalesPopupVisible.value;
    console.log('Show sales popup:', isAddSalesPopupVisible.value);
  }

  // Toggle Add customer popup

  function toggleAddCustomerPopup () {
    isAddCustomerPopupVisible.value = !isAddCustomerPopupVisible.value;
    console.log('Show Customers popup:', !isAddSalesPopupVisible.value);
  }

   // Function to set selected customer
   const setSelectedCustomer = (customer) => {
     selectedCustomer.value = customer;
   };

   // handle toggle toggle checkout popup
   function toggleCheckoutPopup () {
     isCheckoutPopupVisible.value =!isCheckoutPopupVisible.value;
     console.log('Show checkout popup:', !isCheckoutPopupVisible.value);
   }

   // handle toggle show comment modal
   function toggleShowCommentsModal () {
     showCommentsModal.value = !showCommentsModal.value;
     console.log('Show comments modal:', !showCommentsModal.value);
   }

   function toggleAddCommentButtonMoved () {
     isAddCommentButtonMoved.value =!isAddCommentButtonMoved.value;
   }

   function toggleAddManagerApprovalButtonMoved () {
     isAddManagerApprovalButtonMoved.value =!isAddManagerApprovalButtonMoved.value;
   }

   function toggleWalkInCustomerButtonMoved () {
     isWalkInCustomerButtonMoved.value =!isWalkInCustomerButtonMoved.value;
   }

   const isBarcodeSearchFromBackend = ref(false);
   const isRetailSkuSearchFromBackend = ref(false);
   
   const isOnlyManagerLoggedIn = computed(() => {
      return isManagerLoggedIn.value && !isUserLoggedIn.value;
   });

  // Branch context availability for SignalR
  const hasBranchContext = computed(() => !!branchId);

  const isManualOrderPopupVisible = ref(false);

  // keep canDoEmbedded in sync with login state changes (no reload needed)
  watch([isUserLoggedIn, isManagerLoggedIn], () => {
    refreshCanDoEmbedded();
  });

  function toggleManualOrderPopup() {
    isManualOrderPopupVisible.value = !isManualOrderPopupVisible.value;
  }

  const isRefundPopupVisible = ref(false);
  function toggleRefundPopup() {
    isRefundPopupVisible.value = !isRefundPopupVisible.value;
  }

  const isCheckInventoryPopupVisible = ref(false);
  function toggleCheckInventoryPopup() {
    isCheckInventoryPopupVisible.value = !isCheckInventoryPopupVisible.value;
  }

  function isTokenExpired(jwt) {
    if (!jwt || typeof jwt !== 'string') return true;           
    const [, payload] = jwt.split('.');                         
    if (!payload) return true;

    try {
      const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      const exp = decoded.exp;                                  
      if (typeof exp !== 'number') return true;

      const now = Math.floor(Date.now() / 1000);                
      return now >= exp;                                        
    } catch {
      return true;                                              
    }
  }

  const modeMap = {
    Cash: 1,
    DebitCard: 2,
    Visa: 3,
    MasterCard: 4,
    Amex: 5,
    InteracEtransfer: 6,
    StoreCredit: 7,
    // originalPayment: 3,
  };
  
  const selectedRefundMethod = ref(null);
  const toggleBarberItems = () => {
    isBarberItemsPopupVisible.value = !isBarberItemsPopupVisible.value;
  };

  function toggleAddManualOrderPopup() {
    isAddManualOrderPopupVisible.value = !isAddManualOrderPopupVisible.value;
  }

  function togglePrintLabelPopup() {
    printLabelPopupVisible.value = !printLabelPopupVisible.value;
    console.log('Show print label popup:', printLabelPopupVisible.value);
  }

  function toggleBarcodeGenerator() {
    barcodeGeneratorVisible.value = !barcodeGeneratorVisible.value;
  }

  function toggleErrorLogViewer() {
    isErrorLogViewerVisible.value = !isErrorLogViewerVisible.value;
  }

  //Opening and closing screen state
  const isSkeletonPopupVisible = ref(false);
  const cashierName = ref('');
  const posProfile = ref('');
  const today = ref('');
  const getTodayDate = () => {
    const timeZone = ref(Intl.DateTimeFormat().resolvedOptions().timeZone);
    const now = ref(new Date().toLocaleString('en-CA', { timeZone: timeZone.value, hour12: false }));
    today.value = now.value.split(' ')[0];
    return today.value;
  };

  const storedDate = localStorage.getItem('lastOpeningDate');
  const date = ref(storedDate || today.value);

  const getPosProfileName = computed(() => {
    if(window.currentBranch && window.currentBranch.Name) {
      posProfile.value = window.currentBranch.Name;
    }
    return posProfile.value;
  });

  const getCasherName = computed(() => {
    if (isUserLoggedIn.value) {
      return currentUser.value;
    }
    if (isManagerLoggedIn.value) {
      return managerUser.value;
    }
    return "";
  });

  // Get Opening Entry Status Api Call
  const openingEntryStatus = ref('');
  const openingFloat = ref(0);
  const openingStatus = ref('');

  async function getOpeningEntryStatus() {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('managerToken');
      if (!token) {
        toast.error('Authorization token not found. Please log in again.');
        return;
      }

      const response = await axios.post('/api/Closing/ClosingStatus', branchId, {
        headers: {
          'store-id': branchId,
          'Authorization': `Bearer ${token}`
        }
      });

      if(response.status === 200) {
        if(response.data.status && response.data.status.toLowerCase() === 'open') {
          openingStatus.value = 'open';
          openingEntryStatus.value = response.data.docname;

          if (response.data.date) {
            date.value = response.data.date;
            localStorage.setItem('lastOpeningDate', response.data.date);
          }

          console.log('Opening Entry string:', openingEntryStatus.value);
          console.log('Opening Entry (Today):', response.data.docname);
        } else {
          openingStatus.value = 'closed';
          openingEntryStatus.value = '';
          console.log('Opening Entry string:', openingEntryStatus.value);
        }
        openingFloat.value = response.data.cashFloat || 0;
        isSkeletonPopupVisible.value = false;
      } else {
        console.error('Failed to fetch opening entry status:', response.error);
        toast.error('Failed to fetch opening entry status.', response.error);
      }

    } catch(error) {
      console.error('Error fetching opening entry status:', error);
      logError({ callStack: error?.stack || String(error), errorDetails: { message: error?.message, httpStatus: error?.response?.status, component: 'authStore', action: 'getOpeningEntryStatus', branchId: branchId.value } });
    }

  }

  const autoLogoutAfterSale = computed(() =>
    !!(window.currentBranch && window.currentBranch.AutoLogout === 1)
  );

  const canBranchDoManualOrder = computed(() => {
    if (window.currentBranch && window.currentBranch.Name) {
      return window.currentBranch.ManualOrdersEnabled;
    }
  });

  return {
    isUserLoggedIn,
    currentUser,
    userRole,
    token,
    usersList,
    isManagerLoggedIn,
    managerUser,
    managerRole,
    managerToken,
    managerUsersList,
    customersList, 
    fetchCashiers,
    fetchManager,
    // fetchCustomers,
    login,
    logout,
    isCashierLoginInput,
    toggleLoginInput,
    isLogoutConfirmationVisible,
    toggleAddBehaviourPopup,
    isAddBehaviourPopup,
    // isAddItemPopup,
    // toggleAddItemPopup,
    isAddManagerApprovalRequest,
    toggleAddManagerApprovalRequest,
    isManagerLoginPopupVisible,
    toggleManagerLoginPopup,
    isAddSalesPopupVisible,
    toggleAddSalesPopup,
    isAddCustomerPopupVisible,
    toggleAddCustomerPopup,
    selectedCustomer,
    setSelectedCustomer,
    isCheckoutPopupVisible,
    toggleCheckoutPopup,
    showCommentsModal,
    toggleShowCommentsModal,
    isAddCommentButtonMoved,
    toggleAddCommentButtonMoved,
    isAddManagerApprovalButtonMoved,
    toggleAddManagerApprovalButtonMoved,
    isWalkInCustomerButtonMoved,
    toggleWalkInCustomerButtonMoved,
    setLastUser,
    salesPersons,
    branchId,
    managerToken,
    isBarcodeSearchFromBackend,
    isRetailSkuSearchFromBackend,
    isCurrentUserManager,
    refreshAuthState,
    isOnlyManagerLoggedIn,
    isManualOrderPopupVisible,
    toggleManualOrderPopup,
    isRefundPopupVisible,
    toggleRefundPopup,
    isCheckInventoryPopupVisible,
    toggleCheckInventoryPopup,
    isTokenExpired,
    modeMap,
    selectedRefundMethod,
    isBarberItemsPopupVisible,
    toggleBarberItems,
    isAddManualOrderPopupVisible,
    toggleAddManualOrderPopup,
    isOpeningBalacePopupVisible,
    isClosingBalacePopupVisible,
    cashierName,
    posProfile,
    getPosProfileName,
    getCasherName,
    date,
    openingEntryStatus,
    getOpeningEntryStatus,
    openingFloat,
    openingStatus,
    isSkeletonPopupVisible,
    today,
    canDoEmbedded,
    printLabelPopupVisible,
    togglePrintLabelPopup,
    barcodeGeneratorVisible,
    toggleBarcodeGenerator,
    isErrorLogViewerVisible,
    toggleErrorLogViewer,
    getActiveToken,
    lastUpdatedOn,
    setLastUpdatedOn,
    resetLastUpdatedOnForNewDay,
    hasBranchContext,

    canBranchDoManualOrder,
    getTodayDate,
    autoLogoutAfterSale,
  };
});